import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  Ban,
  CheckSquare,
  Info,
  Mail,
  Pencil,
  Phone,
  PhoneOutgoing,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { EntityAvatar } from '@/components/entity-avatar';
import { SidebarTabs } from '@/components/sidebar-tabs';
import { Menu } from '@/components/menu';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { SkeletonList } from '@/components/skeleton';
import { StatusPill } from '@/components/status-dot';
import { ActivityFeed, toFeedItem } from '@/components/activity-feed';
import { LogActivityDialog } from '@/components/log-activity-dialog';
import {
  useContact,
  useContactActivity,
  useDeleteContactActivity,
} from '@/lib/contact-activity';
import { KIND_LABELS, kindTone, useDeleteDirectoryContact } from '@/lib/contacts-directory';
import { formatDateTime, formatPhone, formatStatus } from '@/lib/format';
import { useSoftphone } from '@/lib/softphone';
import { SOURCE_LABEL, stageTone } from '@/routes/leads/index';
import { EditContactDialog } from './index';
import type { ContactDetailData } from '@/types/api';

export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const contactId = id ? Number(id) : undefined;
  const { data, isLoading } = useContact(contactId);
  const [editing, setEditing] = useState(false);
  const sp = useSoftphone();
  const navigate = useNavigate();
  const del = useDeleteDirectoryContact();
  const confirm = useConfirm();
  const toast = useToast();

  if (isLoading || !data) {
    return <SkeletonList rows={4} />;
  }

  const detail: ContactDetailData = data;
  const contact = detail.contact;
  const openTaskCount = detail.open_tasks.length;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <EntityAvatar name={contact.name} size="lg" />
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{contact.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge tone={kindTone(contact.kind)}>
                {KIND_LABELS[contact.kind] ?? contact.kind}
              </Badge>
              {contact.client_id && detail.client_name ? (
                <Link
                  to={`/clients/${contact.client_id}`}
                  className="hover:text-foreground hover:underline"
                >
                  {detail.client_name}
                </Link>
              ) : null}
              {contact.title ? <span>{contact.title}</span> : null}
              {contact.is_blocked ? (
                <Badge tone="danger">
                  <Ban className="size-3" /> Blocked
                </Badge>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {contact.primary_phone ? (
            <Button variant="secondary" onClick={() => sp.makeCall(contact.primary_phone as string)}>
              <PhoneOutgoing className="size-3.5" /> Call
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => setEditing(true)}>
            <Pencil className="size-3.5" /> Edit
          </Button>
          <Menu
            items={[
              {
                key: 'delete',
                label: 'Delete contact',
                icon: Trash2,
                danger: true,
                // The portal sign-in resolves through this record, so the
                // server refuses it; say so here instead of failing the call.
                disabled: contact.has_portal_access,
                onSelect: async () => {
                  const ok = await confirm({
                    title: `Delete ${contact.name}?`,
                    body: contact.is_blocked
                      ? 'Their calls and texts stay in the inbox, listed under the bare number again. This number will no longer be blocked, so a new call from it rings through.'
                      : 'Their calls, texts and payments stay on file, listed under the bare number again rather than their name.',
                    confirmLabel: 'Delete contact',
                    tone: 'danger',
                  });
                  if (ok) {
                    await del.mutateAsync(contact.id);
                    toast.success(`${contact.name} deleted`);
                    navigate('/contacts');
                  }
                },
              },
            ]}
          />
        </div>
      </div>

      <SidebarTabs
        paramName="tab"
        tabs={[
          {
            key: 'activity',
            label: 'Activity',
            icon: Activity,
            content: <ContactActivityTab contactId={contact.id} />,
          },
          {
            key: 'details',
            label: 'Details',
            icon: Info,
            content: <DetailsTab detail={detail} />,
          },
          {
            key: 'pipeline',
            label: 'Leads & tasks',
            icon: CheckSquare,
            count: openTaskCount,
            content: <PipelineTab detail={detail} />,
          },
        ]}
      />

      {editing ? (
        <EditContactDialog contact={contact} onClose={() => setEditing(false)} />
      ) : null}
    </div>
  );
}

function ContactActivityTab({ contactId }: { contactId: number }) {
  const { data, isLoading } = useContactActivity(contactId);
  const [logging, setLogging] = useState(false);
  const del = useDeleteContactActivity(contactId);
  const confirm = useConfirm();
  const toast = useToast();

  // Only the notes logged here are removable. Calls, texts and web inquiries in
  // this feed belong to the inboxes that recorded them.
  const items = useMemo(
    () =>
      (data ?? []).map((item) =>
        toFeedItem(item, {
          onDelete: async (activityId) => {
            const ok = await confirm({
              title: 'Delete this note?',
              body: 'It is removed from this feed for good.',
              confirmLabel: 'Delete note',
              tone: 'danger',
            });
            if (ok) {
              await del.mutateAsync(activityId);
              toast.success('Note deleted');
            }
          },
        }),
      ),
    [data, confirm, del, toast],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Every call, text, note, and web inquiry with this person, newest first.
        </p>
        <Button onClick={() => setLogging(true)}>
          <Plus className="size-4" /> Log activity
        </Button>
      </div>

      <Card>
        <CardBody>
          {isLoading ? (
            <SkeletonList rows={4} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={Activity}
              title="Nothing yet"
              description="Calls, texts, and notes will show up here as they happen."
            />
          ) : (
            <ActivityFeed items={items} />
          )}
        </CardBody>
      </Card>

      {logging ? (
        <LogActivityDialog contactId={contactId} onClose={() => setLogging(false)} />
      ) : null}
    </div>
  );
}

function DetailsTab({ detail }: { detail: ContactDetailData }) {
  const contact = detail.contact;

  return (
    <Card>
      <CardBody className="space-y-5">
        <Field label="Phone numbers" icon={Phone}>
          {contact.phones.length === 0 ? (
            <p className="text-sm text-muted-foreground">None on file.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {contact.phones.map((p) => (
                <li key={p.id} className="flex items-center gap-2">
                  <a href={`tel:${p.number}`} className="hover:underline">
                    {formatPhone(p.number)}
                  </a>
                  <span className="text-xs text-muted-foreground">{formatStatus(p.label)}</span>
                  {p.is_primary ? <Badge tone="neutral">Primary</Badge> : null}
                </li>
              ))}
            </ul>
          )}
        </Field>

        <Field label="Email addresses" icon={Mail}>
          {contact.emails.length === 0 ? (
            <p className="text-sm text-muted-foreground">None on file.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {contact.emails.map((e) => (
                <li key={e.id} className="flex items-center gap-2">
                  <a href={`mailto:${e.address}`} className="hover:underline">
                    {e.address}
                  </a>
                  <span className="text-xs text-muted-foreground">{formatStatus(e.label)}</span>
                  {e.is_primary ? <Badge tone="neutral">Primary</Badge> : null}
                </li>
              ))}
            </ul>
          )}
        </Field>

        {contact.notes ? (
          <Field label="Notes" icon={Info}>
            <p className="whitespace-pre-wrap text-sm text-foreground/90">{contact.notes}</p>
          </Field>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Field({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: typeof Phone;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="size-3.5" /> {label}
      </h3>
      {children}
    </div>
  );
}

function PipelineTab({ detail }: { detail: ContactDetailData }) {
  if (detail.leads.length === 0 && detail.open_tasks.length === 0) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            icon={Sparkles}
            title="No pipeline"
            description="This person has no leads or follow-up tasks."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {detail.leads.length > 0 ? (
        <Card>
          <CardBody className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Leads
            </h3>
            <ul className="divide-y divide-border/70">
              {detail.leads.map((lead) => (
                <li key={lead.id} className="flex items-center gap-2 py-2 text-sm">
                  <Link
                    to={`/leads/${lead.id}`}
                    className="font-medium hover:text-primary hover:underline"
                  >
                    {lead.company_name ?? lead.name}
                  </Link>
                  <StatusPill tone={stageTone(lead.stage)}>{formatStatus(lead.stage)}</StatusPill>
                  <Badge tone="neutral">{SOURCE_LABEL[lead.source] ?? lead.source}</Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatDateTime(lead.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {detail.open_tasks.length > 0 ? (
        <Card>
          <CardBody className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Open tasks
            </h3>
            <ul className="divide-y divide-border/70">
              {detail.open_tasks.map((task) => (
                <li key={task.id} className="flex items-center gap-2 py-2 text-sm">
                  <Link
                    to={`/leads/${task.lead_id}?tab=tasks`}
                    className="hover:text-primary hover:underline"
                  >
                    {task.title}
                  </Link>
                  {task.is_overdue ? <Badge tone="danger">Overdue</Badge> : null}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatDateTime(task.due_at)}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
